import { RightOutlined } from '@ant-design/icons'
import { Collapse, ConfigProvider, InputNumber, Slider } from 'antd'
import { useEffect, useState } from 'react'

import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { CharacteristicFilterWithRange } from '@/types'

import generalCn from '../GeneralFiltersStyles.module.scss'
import cn from './FilterRangeSlider.module.scss'

type Props = {
  item: CharacteristicFilterWithRange
  selectedItem?: CharacteristicFilterWithRange
  onChange: (range: { min: number | null; max: number | null } | null) => void
  forceOpen?: boolean
}

const FilterRangeSlider = ({
  item,
  selectedItem,
  onChange,
  forceOpen,
}: Props) => {
  const { selectedOrganisation } = useUserInfoStore((state) => state)
  const isOrgSelected = Object.keys(selectedOrganisation).length > 0

  const unit = item.unitOfMeasurementDto?.description || ''
  const avMin = item.min ?? 0
  const avMax = item.max ?? 1000

  const selMin = selectedItem?.chosenRange?.min ?? null
  const selMax = selectedItem?.chosenRange?.max ?? null

  const isApplied = selMin !== null || selMax !== null

  const hasIntersection =
    selMin !== null && selMax !== null && !(selMin > avMax || selMax < avMin)

  const count = hasIntersection
    ? ((item as CharacteristicFilterWithRange & { count?: number }).count ?? '')
    : 0
  const primaryColor = isOrgSelected ? '#3bb0d0' : '#ea4a1b'

  const [sliderValues, setSliderValues] = useState<number[]>([avMin, avMax])

  useEffect(() => {
    setSliderValues([selMin ?? avMin, selMax ?? avMax])
  }, [selMin, selMax, avMin, avMax])

  const formatName = (name: string) => name.replace(/\sP(\s|$)/, '$1').trim()

  const handleSliderChange = (values: number[]) => {
    setSliderValues(values)
    onChange({ min: values[0], max: values[1] })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isControlKey = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
    ].includes(e.key)

    if (isControlKey) return

    const value = (e.target as HTMLInputElement).value

    if (e.key === '.') {
      const cleanValue = value.replace(/\s/g, '')
      if (cleanValue.includes('.')) {
        e.preventDefault()
        return
      }
    }

    if (!/[\d.]/.test(e.key)) {
      e.preventDefault()
    }
  }

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
  }

  const handleMinChange = (v: number | null) => {
    if (v === null || Number.isNaN(v)) {
      onChange({ min: null, max: selMax ?? null })
      return
    }

    const currentMax = selMax ?? avMax
    const clamped = Math.max(avMin, Math.min(v, currentMax))
    onChange({ min: clamped, max: currentMax })
  }

  const handleMaxChange = (v: number | null) => {
    if (v === null || Number.isNaN(v)) {
      onChange({ min: selMin ?? null, max: null })
      return
    }

    const currentMin = selMin ?? avMin
    const clamped = Math.min(avMax, Math.max(v, currentMin))
    onChange({ min: currentMin, max: clamped })
  }

  const normalizedMin: number | undefined = selMin === null ? undefined : selMin
  const normalizedMax: number | undefined = selMax === null ? undefined : selMax

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: primaryColor,
          borderRadius: 4,
        },
      }}
    >
      <div
        className={[
          generalCn.filterContainer,
          isApplied && !hasIntersection ? cn.noIntersection : '',
        ].join(' ')}
      >
        <div className={generalCn.collapseContainer}>
          <Collapse
            {...(forceOpen
              ? { activeKey: [item.code] }
              : { defaultActiveKey: isApplied ? [item.code] : [] })}
            expandIcon={({ isActive }) => (
              <RightOutlined
                className={`${generalCn.expandIcon} ${isActive ? generalCn.expandIconActive : ''}`}
              />
            )}
            items={[
              {
                key: item.code,
                label: (
                  <div className={generalCn.collapseHeaderContainer}>
                    <span className={generalCn.filterName}>
                      {formatName(item.name)}{' '}
                      {count !== '' && (
                        <small className={generalCn.filterCount}>
                          {/* ({count}) */}
                        </small>
                      )}
                    </span>
                    {isApplied && (
                      <button
                        className={generalCn.resetButton}
                        onClick={handleReset}
                      >
                        Сбросить
                      </button>
                    )}
                  </div>
                ),
                children: (
                  <div className={cn.sliderWrapper}>
                    <div className={cn.inputs}>
                      <div className={cn.inputGroup}>
                        <span className={cn.label}>
                          От{unit ? `, ${unit}` : ''}
                        </span>
                        <InputNumber
                          controls={false}
                          min={avMin}
                          max={avMax}
                          value={normalizedMin ?? undefined}
                          placeholder={String(avMin)}
                          step={0.01}
                          precision={1}
                          onKeyDown={handleKeyDown}
                          onChange={
                            handleMinChange as (value: number | null) => void
                          }
                        />
                      </div>

                      <div className={cn.inputGroup}>
                        <span className={cn.label}>
                          До{unit ? `, ${unit}` : ''}
                        </span>
                        <InputNumber
                          controls={false}
                          min={avMin}
                          max={avMax}
                          value={normalizedMax ?? undefined}
                          placeholder={String(avMax)}
                          step={0.01}
                          precision={1}
                          onKeyDown={handleKeyDown}
                          onChange={
                            handleMaxChange as (value: number | null) => void
                          }
                        />
                      </div>
                    </div>

                    {avMin !== avMax && (
                      <Slider
                        range
                        min={avMin}
                        max={avMax}
                        value={sliderValues}
                        onChange={handleSliderChange}
                        tooltip={{ open: false }}
                      />
                    )}

                    {isApplied && !hasIntersection && (
                      <div className={cn.warningText}>
                        Нет товаров в выбранном диапазоне
                      </div>
                    )}
                  </div>
                ),
              },
            ]}
          />
        </div>
      </div>
    </ConfigProvider>
  )
}

export default FilterRangeSlider
